import { ApiReportTemplateSectionPageLocationChoices } from "@api/graphql/types-and-hooks";

/**
 * Single source of truth for "does this report's *template* define fields for
 * a given page location (e.g. Conclusions)?"
 *
 * Generated report data (ReportSectionsType rows) can't answer this: a report
 * has zero generated sections for a page location both when nothing has been
 * generated yet *and* when the template never defines that page at all.
 * `conclusionsSections.length === 0` conflates those two states. Only the
 * template's own section definitions (ReportTemplateSectionType, fetched via
 * `reportTemplate.reportTemplateSections`) can distinguish them -- so any
 * "should Conclusions render / be offered" check must go through here rather
 * than an org-name check (e.g. `isKuwaitUser`) or a generated-sections count.
 * See PROA-1659.
 */

export interface ReportTemplateSectionFieldsShape {
  totalCount?: number | null;
}

export interface ReportTemplateSectionShape {
  pageLocation?: ApiReportTemplateSectionPageLocationChoices | string | null;
  reportTemplateSectionFields?: ReportTemplateSectionFieldsShape | null;
}

export interface ReportTemplateSectionsConnectionShape {
  edges?: ({ node?: ReportTemplateSectionShape | null } | null)[] | null;
}

export interface ReportTemplateWithSectionsShape {
  reportTemplateSections?: ReportTemplateSectionsConnectionShape | null;
}

/** True when `template` defines at least one field for `pageLocation`. */
export const templateDefinesPageLocationFields = (
  template: ReportTemplateWithSectionsShape | null | undefined,
  pageLocation: ApiReportTemplateSectionPageLocationChoices,
): boolean =>
  (template?.reportTemplateSections?.edges ?? []).some(
    (edge) =>
      edge?.node?.pageLocation === pageLocation && (edge?.node?.reportTemplateSectionFields?.totalCount ?? 0) > 0,
  );

/** True when `template` defines at least one Conclusions field. */
export const templateHasConclusionsFields = (template: ReportTemplateWithSectionsShape | null | undefined): boolean =>
  templateDefinesPageLocationFields(template, ApiReportTemplateSectionPageLocationChoices.Conclusions);
